import React, {useCallback, useEffect, useLayoutEffect, useRef, useState} from "react";
import {BlueprintLocation, Building, PhotoModel} from "../types/protocols";
import {BaseComponentImpl, RoomImpl} from "../types/implementations";
import {useFirebaseImage} from "../Helpers/ImageLoader";
import COLORS from "../Helpers/utils";
import {BaseType, RoomType} from "../types/Enums";
import { CircleCheck, KeyRound, Plug, Zap } from "lucide-react";
import {useBuildComponentTypes} from "../contexts/BuildComponentTypesContext";
import {getComponentIconPaths} from "../Helpers/componentTypeIconUtils";
import {locationFromClientPoint as locationFromClientPointMath} from "./blueprintPlacementMath";
import {usePdfDocument} from "../hooks/pdf/usePdfDocument";
import PdfPageCanvas from "./pdf/PdfPageCanvas";
import {fitMarkerLabel, LabelFit} from "./markerGeometry";
import {markerNumber} from "./markerNumber";
import LocationPin from "./LocationPin";
import MaskedIcon from "./MaskedIcon";
import "./BlueprintWithMarkers.css";

/** Gap between the icon marker and its hover label, in the marker's own px. */
const LABEL_GAP = 6;

/** White outline width of icon markers, in px. */
const MARKER_BORDER = 3;

/**
 * Pointy-top regular hexagon (the outline of a box seen from an angle) for
 * room markers. `width` is the outer width including the border; the shape is
 * inset by half the stroke so the border stays inside the box. Returns the
 * box height and the SVG polygon points.
 */
function roomMarkerHexagon(width: number): { height: number; points: string } {
    const height = (width * 2) / Math.sqrt(3);
    const cx = width / 2;
    const cy = height / 2;
    const apothem = width / 2 - MARKER_BORDER / 2;
    const r = (apothem * 2) / Math.sqrt(3);
    const points = [
        [cx, cy - r],
        [cx + apothem, cy - r / 2],
        [cx + apothem, cy + r / 2],
        [cx, cy + r],
        [cx - apothem, cy + r / 2],
        [cx - apothem, cy - r / 2]
    ].map(([x, y]) => `${x},${y}`).join(' ');
    return { height, points };
}

/** Fixed zoom ceiling (D20): the vector layer stays sharp at any magnification, so it is no longer tied to raster pixels. */
export const BLUEPRINT_MAX_ZOOM = 20;
const MIN_ZOOM = 0.5;

/** One marker to single out; `kind` follows the marker groups (rooms, utilities = components, spots = numbered). */
export interface BlueprintHighlight {
    kind: 'room' | 'utility' | 'spot';
    key: string;
}

interface BlueprintWithMarkersProps {
    blueprint: PhotoModel;
    currentBlueprintKey: string;
    building: Building;
    rooms?: RoomImpl[];
    utilities?: BaseComponentImpl[];
    selectedRoomKey?: string | null;
    selectedUtilityKey?: string | null;
    /** Several selected component markers at once (multi-select pickers); adds to `selectedUtilityKey`. */
    selectedUtilityKeys?: ReadonlySet<string>;
    /** Component markers drawn faded and without a click (context only, e.g. outside a picker's filter). */
    mutedUtilityKeys?: ReadonlySet<string>;
    /** The click event is passed so a caller that opens a page can honour Cmd/Ctrl/middle-click (`useOpenPath`). */
    onRoomClick?: (roomKey: string, event?: React.MouseEvent) => void;
    onUtilityClick?: (utilityKey: string, event?: React.MouseEvent) => void;
    /** The room or component marker under the mouse, null when it leaves (e.g. to highlight its list row). */
    onRoomHover?: (roomKey: string | null) => void;
    onUtilityHover?: (utilityKey: string | null) => void;
    title?: string;
    /** Omit the title heading entirely (the caller renders its own header). */
    hideTitle?: boolean;
    /** Smaller room and component icon pins, for dense photos like Fasad/tak. */
    compactMarkers?: boolean;
    singleRoom?: {
        room: RoomImpl;
        blueprintLocation: BlueprintLocation;
    };
    disableZoom?: boolean;
    /** Numbered markers (parking spots, storage units, units), keyed by currentBlueprintKey in blueprintLocations. */
    spots?: NumberedMarker[];
    selectedSpotKey?: string | null;
    onSpotClick?: (spotKey: string, event?: React.MouseEvent) => void;
    /** The numbered marker under the mouse, null when it leaves (e.g. to highlight its list row). */
    onSpotHover?: (spotKey: string | null) => void;
    /**
     * Placement mode: a click on the drawing (not a drag, not a marker) reports
     * the clicked point as a BlueprintLocation in the drawing's pixel space with
     * the node's `width`/`height` as reference dimensions (plan §3.3) — the same
     * convention as iOS. The point is a fraction of the drawn box, whatever is
     * drawn in it (raster or vector).
     */
    placementMode?: boolean;
    onBlueprintClick?: (location: BlueprintLocation) => void;
    /** A red map pin on a point (no icon, no click), e.g. the picked point in the building-check picker or a component's position. */
    pointMarker?: BlueprintLocation;
    /** Accessible name of the point marker. Default "Vald plats". */
    pointMarkerLabel?: string;
    /**
     * Move mode: while set, spot markers can be dragged; dropping one reports
     * the new location (same pixel convention as onBlueprintClick). A press
     * that moves less than a few pixels still counts as a click. Pages turn
     * this on and off explicitly so the mode has a clear start and end.
     */
    onSpotMove?: (spotKey: string, location: BlueprintLocation) => void;
    /** Enlarges this marker and dims every other one (blueprint relocation wizard). */
    highlight?: BlueprintHighlight;
}

/** Glyphs a numbered marker can show in its corners. */
export type NumberedMarkerGlyph = 'charger' | 'outlet';

/**
 * Minimal shape of a numbered marker (parking spot, storage unit, unit); pages
 * map their own type to this and supply the colour (storage-units plan D13).
 */
export interface NumberedMarker {
    key: string;
    number: string;
    /** Fill colour, e.g. spotStatusColor(spot) or unitStatusColor(unit). */
    color: string;
    /**
     * Type icon URL (parking: spotTypeIcon(spot.type), units: unitTypeIcon(unit.kind)).
     * When set it fills the circle, the number moves to a white pill centred
     * under it that grows to fit, the colour carries the status, and the rented key, check
     * and fading are left out; the hover label still says Uthyrd/Ledig.
     */
    icon?: string;
    /** True: `icon` is a colour image drawn as it is (units, storage units); false: a glyph drawn white (parking). */
    iconColored?: boolean;
    /** Corner glyphs (charger, outlet). */
    glyphs?: NumberedMarkerGlyph[];
    /**
     * Lease state, shown as a small icon above the number: true (currentLeaseKey
     * set) a key and a muted ring, false a check. Leave it out for something that
     * is never leased (an own-use or bostadsrätt unit): no icon.
     */
    isRented?: boolean;
    /** Tooltip; defaults to the number. */
    displayName?: string;
    blueprintLocations?: Record<string, BlueprintLocation>;
    /** False keeps the marker fixed in move mode (e.g. a unit pin derived from its rooms). Default true. */
    movable?: boolean;
}

interface MarkerData {
    key: string;
    blueprintLocation: BlueprintLocation;
    type: 'room' | 'utility' | 'position' | 'numbered';  // 'position' is a map pin on a point, 'numbered' a labelled marker (spot, storage unit)
    baseType?: BaseType;  // For 'utility' markers: the component's base type (drives color)
    spot?: NumberedMarker;    // For 'numbered' markers: number, colour and glyphs
    displayName: string;
    isSelected: boolean;  // Controls enlargement
    dimmed?: boolean;     // Faded: another marker is highlighted
    iconPath: string;
    fallbackIconPaths: string[]; // Fallback paths to try if primary fails
    onClick?: (event: React.MouseEvent) => void;
    onDragStart?: (e: React.MouseEvent) => void;  // spot markers in move mode
    onHoverChange?: (hovered: boolean) => void;
    baseZIndex: number;
    markerSize: number; // Base size for the marker
    iconSize: number; // Size for the icon inside
    padding: number; // Padding around the icon
}

// Type guard for components with blueprintLocations
interface ComponentWithBlueprintLocations extends BaseComponentImpl {
    blueprintLocations: Record<string, BlueprintLocation>;
}

function hasBlueprintLocations(component: BaseComponentImpl): component is ComponentWithBlueprintLocations {
    return 'blueprintLocations' in component &&
           typeof (component as any).blueprintLocations === 'object' &&
           (component as any).blueprintLocations !== null;
}

// Marker icon with fallback support.
// Defined at module scope (not inside BlueprintWithMarkers) so its identity is
// stable across renders. If it were redefined every render, panning would
// remount each <img>, forcing fallback icons to re-run their error→fallback
// load on every frame — which shows up as flicker.
const MarkerIcon: React.FC<{
    iconPath: string;
    fallbackPaths: string[];
    displayName: string;
    iconSize: number;
}> = ({ iconPath, fallbackPaths, displayName, iconSize }) => {
    const [currentSrc, setCurrentSrc] = useState(iconPath);
    const [fallbackIndex, setFallbackIndex] = useState(0);

    // Reset when iconPath changes
    React.useEffect(() => {
        setCurrentSrc(iconPath);
        setFallbackIndex(0);
    }, [iconPath]);

    const handleError = () => {
        if (fallbackIndex < fallbackPaths.length) {
            setCurrentSrc(fallbackPaths[fallbackIndex]);
            setFallbackIndex(prev => prev + 1);
        }
    };

    return (
        <img
            src={currentSrc}
            alt={displayName}
            style={{
                width: `${iconSize}px`,
                height: `${iconSize}px`,
                objectFit: 'contain',
                pointerEvents: 'none'
            }}
            onLoad={(e) => {
                (e.target as HTMLImageElement).style.display = 'block';
            }}
            onError={handleError}
        />
    );
};

/** A numbered marker's status icon: a key when rented, a check when free, none when it is never leased. */
const StatusIconFor = (isRented: boolean | undefined) =>
    isRented === undefined ? null : isRented ? KeyRound : CircleCheck;

// Generic marker component. Also module-scoped for stable identity across
// renders; position and zoom are passed in (both are pan-independent, so a
// pan re-render reconciles the same DOM rather than remounting it).
const Marker: React.FC<{
    data: MarkerData;
    position: { left: string; top: string } | null;
    zoom: number;
    /** The clipping container (`overflow: hidden`); hover labels are kept inside it. */
    containerRef: React.RefObject<HTMLDivElement | null>;
}> = ({ data, position, zoom, containerRef }) => {
    // Calculate inverse scale to keep marker size constant
    const inverseScale = 1 / zoom;
    // Enlarge when selected (shown in preview pane)
    // Numbered markers grow a little less when selected; they carry text
    const selectedScale = data.isSelected ? (data.type === 'numbered' ? 1.4 : 1.5) : 1;

    // Hover label fit. The label is centered under the icon and would be cut
    // off by the container near its edges, so on hover it is measured against
    // the container and shifted sideways or flipped above the icon.
    const iconRef = useRef<HTMLDivElement>(null);
    const labelRef = useRef<HTMLSpanElement>(null);
    const [hovered, setHovered] = useState(false);
    const [labelFit, setLabelFit] = useState<LabelFit | null>(null);
    useLayoutEffect(() => {
        if (!hovered) {
            setLabelFit(null);
            return;
        }
        const container = containerRef.current;
        const icon = iconRef.current;
        const label = labelRef.current;
        if (!container || !icon || !label) return;
        // Screen geometry: the wrapper's inverse zoom cancels the image zoom, so
        // the icon renders at selectedScale and the counter-scaled label at 1.
        // The icon scales around its centre, which makes the centre stable even
        // mid-transition; everything else is derived from layout sizes.
        const s = selectedScale;
        const iconRect = icon.getBoundingClientRect();
        const cx = (iconRect.left + iconRect.right) / 2;
        const cy = (iconRect.top + iconRect.bottom) / 2;
        const w = label.offsetWidth;
        const h = label.offsetHeight;
        const gap = (icon.offsetHeight / 2 + LABEL_GAP) * s;
        // The counter-scale shrinks the label's layout box around its own centre,
        // which moves its visible edge inwards by half the difference.
        const shrink = (h * (s - 1)) / 2;
        const belowTop = cy + gap + shrink;
        const aboveBottom = cy - gap - shrink;
        const c = container.getBoundingClientRect();
        setLabelFit(fitMarkerLabel(
            { left: c.left, top: c.top, right: c.right, bottom: c.bottom },
            { left: cx - w / 2, right: cx + w / 2, top: belowTop, bottom: belowTop + h },
            { left: cx - w / 2, right: cx + w / 2, top: aboveBottom - h, bottom: aboveBottom }
        ));
    }, [hovered, selectedScale, zoom, containerRef]);

    if (!position) return null;

    const handleMarkerClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (data.onClick) {
            data.onClick(e);
        }
    };
    // A middle click is reported too, so a marker that opens a page can open it in a new tab
    const handleMarkerAuxClick = (e: React.MouseEvent) => {
        if (e.button === 1) handleMarkerClick(e);
    };

    // The hover label under (or above) the marker. Counter-scaled to keep its size;
    // the fit shift comes last in the transform so it is in the counter-scaled
    // (screen) px the measurement was made in.
    const hoverLabel = (text: string) => (
        <span
            ref={labelRef}
            className="marker-label"
            style={{
                position: 'absolute',
                top: labelFit?.above ? 'auto' : '100%',
                bottom: labelFit?.above ? '100%' : 'auto',
                left: '50%',
                transform: `translateX(-50%)`
                    + (data.isSelected ? ` scale(${1 / selectedScale})` : '')
                    + (labelFit?.dx ? ` translateX(${labelFit.dx}px)` : ''),
                marginTop: labelFit?.above ? 0 : `${LABEL_GAP}px`,
                marginBottom: labelFit?.above ? `${LABEL_GAP}px` : 0,
                // The numbered marker's bold, tight number text is not the label's
                fontWeight: 400,
                lineHeight: 1.4
            }}
        >
            {text}
        </span>
    );

    // Position marker: the red map pin (same as on photos), tip on the point.
    // Counter-scaled so it keeps its size while zooming.
    if (data.type === 'position') {
        return (
            <LocationPin
                left={position.left}
                top={position.top}
                scale={inverseScale}
                label={data.displayName || 'Markering'}
            />
        );
    }

    // Numbered marker (parking spot, storage unit, unit): a fixed-size circle with
    // a status icon (key when rented, check when free) above the number part of
    // the number field, colour from the page, small corner glyphs (charger,
    // outlet) stacked at the top-right. Long numbers get smaller text, never a
    // wider marker. A marker with an `icon` shows the type icon instead (white
    // glyph or colour image), its colour alone carries the status, and the
    // number sits in a white pill centred at the bottom, as wide as the number,
    // outlined in that colour.
    if (data.type === 'numbered' && data.spot) {
        const label = markerNumber(data.spot.number);
        const icon = data.spot.icon;
        // An icon marker shows its status by colour alone (parking)
        const StatusIcon = icon ? null : StatusIconFor(data.spot.isRented);
        const fadedAsRented = !icon && data.spot.isRented;
        // Smaller text for longer numbers so five digits still fit the circle
        const fontSize = label.length <= 3 ? (StatusIcon ? 11 : 13) : label.length === 4 ? 9 : 8;
        const badgeStyle: React.CSSProperties = {
            position: 'absolute',
            width: '16px',
            height: '16px',
            borderRadius: '50%',
            backgroundColor: 'white',
            border: '1px solid #ddd',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#333'
        };
        return (
            <div
                className={`marker-wrapper ${data.isSelected ? "highlight-marker" : ""}`}
                style={{
                    ...position,
                    position: 'absolute',
                    display: 'inline-block',
                    transform: `translate(-50%, -50%) scale(${inverseScale * selectedScale})`,
                    zIndex: data.isSelected ? data.baseZIndex + 20 : data.baseZIndex,
                    cursor: data.onDragStart ? 'grab' : 'pointer',
                    opacity: data.dimmed ? 0.3 : undefined,
                    transition: 'transform 0.2s ease-in-out'
                }}
                onClick={handleMarkerClick}
                onAuxClick={handleMarkerAuxClick}
                onMouseDown={data.onDragStart}
                onMouseEnter={() => { setHovered(true); data.onHoverChange?.(true); }}
                onMouseLeave={() => { setHovered(false); data.onHoverChange?.(false); }}
            >
                <div
                    ref={iconRef}
                    className="marker"
                    style={{
                        position: 'relative',
                        boxSizing: 'border-box',
                        width: `${data.markerSize}px`,
                        height: `${data.markerSize}px`,
                        padding: 0,
                        borderRadius: '50%',
                        backgroundColor: data.spot.color,
                        border: fadedAsRented ? '3px solid #adb5bd' : '3px solid white',
                        opacity: fadedAsRented ? 0.8 : 1,
                        boxShadow: '0 2px 6px rgba(0, 0, 0, 0.3)',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '1px',
                        color: 'white',
                        fontWeight: 700,
                        fontSize: `${fontSize}px`,
                        lineHeight: 1,
                        whiteSpace: 'nowrap',
                        userSelect: 'none'
                    }}
                >
                    {icon ? (
                        data.spot.iconColored
                            ? <img src={icon} alt="" aria-hidden="true" draggable={false} style={{ width: '20px', height: '20px', objectFit: 'contain', pointerEvents: 'none' }} />
                            : <MaskedIcon src={icon} color="white" size={18} />
                    ) : (
                        <>
                            {StatusIcon && <StatusIcon size={10} strokeWidth={2.5} aria-hidden="true" />}
                            {/* A label that still does not fit (no digits, e.g. "Garage") is cut with an ellipsis */}
                            <span style={{ maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis' }}>{label}</span>
                        </>
                    )}
                    {data.spot.glyphs?.includes('charger') && (
                        <span style={{ ...badgeStyle, top: '-7px', right: '-7px' }}>
                            <Zap size={10} />
                        </span>
                    )}
                    {/* Below the charger */}
                    {data.spot.glyphs?.includes('outlet') && (
                        <span style={{ ...badgeStyle, top: '7px', right: '-9px' }}>
                            <Plug size={10} />
                        </span>
                    )}
                    {icon && data.spot.number.trim() && (
                        <span
                            style={{
                                position: 'absolute',
                                bottom: '-7px',
                                left: '50%',
                                transform: 'translateX(-50%)',
                                boxSizing: 'border-box',
                                minWidth: '16px',
                                height: '16px',
                                padding: '0 4px',
                                borderRadius: '8px',
                                backgroundColor: 'white',
                                border: `1.5px solid ${data.spot.color}`,
                                color: '#333',
                                fontSize: '10px',
                                lineHeight: '13px',
                                textAlign: 'center',
                                whiteSpace: 'nowrap'
                            }}
                        >
                            {label}
                        </span>
                    )}
                </div>
                {/* Outside the circle, so a rented marker's fading leaves the label alone; the
                    wrapper is the circle's size, so it sits the same as on other markers */}
                {hoverLabel(data.spot.isRented === undefined ? data.displayName : `${data.displayName} · ${data.spot.isRented ? 'Uthyrd' : 'Ledig'}`)}
            </div>
        );
    }

    // Regular marker with icon
    // Adjust z-index for selected markers
    const zIndex = data.isSelected ? data.baseZIndex + 20 : data.baseZIndex;

    // Marker background color:
    // - Rooms: yellow (matches Förvaltning medium priority tag)
    // - Utility and fast inredning components: purple (matches Förvaltning "Kontroll")
    // - Other components: primary blue
    let backgroundColor: string;
    if (data.type === 'room') {
        backgroundColor = COLORS.mediumPriorityYellow;
    } else if (data.baseType === BaseType.Utility || data.baseType === BaseType.FixedFurnishing) {
        backgroundColor = COLORS.inspectionPurple;
    } else {
        backgroundColor = COLORS.primary;
    }

    // Rooms are drawn as a hexagon (box seen from an angle) instead of a
    // circle. Same width as the round marker, so the icon fits the same way.
    const isRoom = data.type === 'room';
    const roomWidth = data.iconSize + 2 * data.padding + 2 * MARKER_BORDER;
    const roomShape = isRoom ? roomMarkerHexagon(roomWidth) : null;
    const shadow = data.isSelected ? '0 3px 10px rgba(0, 0, 0, 0.6)' : '0 2px 6px rgba(0, 0, 0, 0.3)';

    return (
        <div
            className={`marker-wrapper ${data.isSelected ? "highlight-marker" : ""}`}
            style={{
                ...position,
                position: 'absolute',
                display: 'inline-block',
                transform: `translate(-50%, -50%) scale(${inverseScale * selectedScale})`,
                zIndex: zIndex,
                cursor: data.onClick ? 'pointer' : 'default',
                opacity: data.dimmed ? 0.3 : undefined,
                transition: 'transform 0.3s ease-in-out'
            }}
            onClick={handleMarkerClick}
            onAuxClick={handleMarkerAuxClick}
            onMouseEnter={() => { setHovered(true); data.onHoverChange?.(true); }}
            onMouseLeave={() => { setHovered(false); data.onHoverChange?.(false); }}
        >
            {/* Icon container with clipping - also serves as positioning context for label */}
            <div
                ref={iconRef}
                className="marker"
                style={roomShape ? {
                    position: 'relative',
                    // Own stacking context so the shape (z-index -1) stays behind the icon
                    isolation: 'isolate',
                    width: `${roomWidth}px`,
                    height: `${roomShape.height}px`,
                    // Override the global .marker rule (LevelPage.css): the SVG draws fill and border
                    padding: 0,
                    border: 'none',
                    borderRadius: 0,
                    backgroundColor: 'transparent',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                } : {
                    position: 'relative',
                    backgroundColor: backgroundColor,
                    padding: `${data.padding}px`,
                    borderRadius: '50%',
                    border: `${MARKER_BORDER}px solid white`,
                    // Darker shadow on the enlarged selected pin so it stands out
                    boxShadow: shadow,
                    transition: 'box-shadow 0.3s ease-in-out',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                }}
            >
                {roomShape && (
                    <svg
                        width={roomWidth}
                        height={roomShape.height}
                        viewBox={`0 0 ${roomWidth} ${roomShape.height}`}
                        aria-hidden
                        style={{
                            position: 'absolute',
                            inset: 0,
                            zIndex: -1,
                            overflow: 'visible',
                            // drop-shadow follows the hexagon; box-shadow would be a rectangle
                            filter: `drop-shadow(${shadow})`,
                            transition: 'filter 0.3s ease-in-out'
                        }}
                    >
                        <polygon
                            points={roomShape.points}
                            fill={backgroundColor}
                            stroke="white"
                            strokeWidth={MARKER_BORDER}
                            strokeLinejoin="round"
                        />
                    </svg>
                )}
                <MarkerIcon
                    iconPath={data.iconPath}
                    fallbackPaths={data.fallbackIconPaths}
                    displayName={data.displayName}
                    iconSize={data.iconSize}
                />
                {hoverLabel(data.displayName)}
            </div>
        </div>
    );
};

const BlueprintWithMarkers: React.FC<BlueprintWithMarkersProps> = ({
                                                                       blueprint,
                                                                       currentBlueprintKey,
                                                                       building,
                                                                       rooms = [],
                                                                       utilities = [],
                                                                       selectedRoomKey,
                                                                       selectedUtilityKey,
                                                                       selectedUtilityKeys,
                                                                       mutedUtilityKeys,
                                                                       onRoomClick,
                                                                       onUtilityClick,
                                                                       onRoomHover,
                                                                       onUtilityHover,
                                                                       title = "Ritning",
                                                                       hideTitle = false,
                                                                       singleRoom,
                                                                       disableZoom = false,
                                                                       compactMarkers = false,
                                                                       spots = [],
                                                                       selectedSpotKey,
                                                                       onSpotClick,
                                                                       onSpotHover,
                                                                       placementMode = false,
                                                                       onBlueprintClick,
                                                                       onSpotMove,
                                                                       pointMarker,
                                                                       pointMarkerLabel = 'Vald plats',
                                                                       highlight
                                                                   }) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const imageContainerRef = useRef<HTMLDivElement>(null);
    const imageRef = useRef<HTMLImageElement>(null);
    // True while the current mouse-down has moved far enough to count as a pan,
    // so the click that follows a drag is not treated as a placement click.
    const dragMovedRef = useRef(false);

    // Spot marker drag (move mode). State drives the dragged marker's position;
    // refs carry the press origin and whether it moved enough to be a drag.
    // `dropped` keeps the override in place after release so the marker stays
    // where it was dropped until the refetched spots carry the new location.
    const [spotDrag, setSpotDrag] = useState<{ key: string; location: BlueprintLocation; dropped: boolean } | null>(null);
    const spotDragRef = useRef<{ key: string; startX: number; startY: number; moved: boolean; location: BlueprintLocation | null } | null>(null);
    const suppressSpotClickRef = useRef(false);
    // Latest callback in a ref so the listener effect is keyed on *whether*
    // move mode is on, not on the callback's identity (a parent re-render must
    // not tear the listeners down and drop the optimistic override).
    const onSpotMoveRef = useRef(onSpotMove);
    useEffect(() => { onSpotMoveRef.current = onSpotMove; }, [onSpotMove]);
    const moveModeOn = !!onSpotMove;
    const [containerDimensions, setContainerDimensions] = useState({ width: 0, height: 0 });
    const { src, isLoading, hasError } = useFirebaseImage(blueprint?.storagePath);
    // Vector layer (D19): only drawings carry `source`; satellite images and photos
    // come through here without one and the <img> is their renderer, no warning.
    const { page: pdfPage } = usePdfDocument(blueprint?.source?.storagePath);
    // Canvas backing stores are large; list pages mount many drawings, so the
    // vector layer exists only while the drawing is near the viewport. The
    // raster stays in place for the rest. No IntersectionObserver = always near.
    const [nearViewport, setNearViewport] = useState(true);
    const { getBaseType } = useBuildComponentTypes();
    
    // Zoom and pan state
    const [zoom, setZoom] = useState(1);
    const [pan, setPan] = useState({ x: 0, y: 0 });
    const [isDragging, setIsDragging] = useState(false);
    const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
    
    const maxZoom = BLUEPRINT_MAX_ZOOM;

    // Refs to access current values in event listeners
    const zoomRef = useRef(zoom);
    const panRef = useRef(pan);

    useEffect(() => {
        zoomRef.current = zoom;
        panRef.current = pan;
    }, [zoom, pan]);


    // Handle mouse drag for panning
    const [dragStartPos, setDragStartPos] = useState({ x: 0, y: 0 });
    const [hasMoved, setHasMoved] = useState(false);
    
    const handleMouseDown = useCallback((e: React.MouseEvent) => {
        // Don't start dragging if clicking on a marker
        if ((e.target as HTMLElement).closest('.marker')) {
            return;
        }
        
        setIsDragging(true);
        setHasMoved(false);
        dragMovedRef.current = false;
        setDragStart({ x: e.clientX - pan.x, y: e.clientY - pan.y });
        setDragStartPos({ x: e.clientX, y: e.clientY });
    }, [pan]);

    const handleMouseMove = useCallback((e: React.MouseEvent) => {
        if (isDragging) {
            const moved = Math.abs(e.clientX - dragStartPos.x) > 3 || Math.abs(e.clientY - dragStartPos.y) > 3;
            if (moved) {
                setHasMoved(true);
                dragMovedRef.current = true;
            }
            
            setPan({
                x: e.clientX - dragStart.x,
                y: e.clientY - dragStart.y
            });
        }
    }, [isDragging, dragStart, dragStartPos]);

    const handleMouseUp = useCallback(() => {
        setIsDragging(false);
        setHasMoved(false);
    }, []);

    // Handle double-click to zoom in
    const handleDoubleClick = useCallback((e: React.MouseEvent) => {
        e.preventDefault();
        
        if (!containerRef.current) return;
        
        const currentZoom = zoomRef.current;
        const currentPan = panRef.current;

        // Multiplicative zoom: 50% increase on double-click
        const newZoom = Math.min(currentZoom * 1.5, BLUEPRINT_MAX_ZOOM);
        
        // Get mouse position relative to the container
        const rect = containerRef.current.getBoundingClientRect();
        const mouseX = e.clientX - rect.left;
        const mouseY = e.clientY - rect.top;
        
        // Calculate the point in the image that's under the mouse
        const pointX = (mouseX - currentPan.x) / currentZoom;
        const pointY = (mouseY - currentPan.y) / currentZoom;
        
        // Calculate new pan to keep that point under the mouse
        const newPan = {
            x: mouseX - pointX * newZoom,
            y: mouseY - pointY * newZoom
        };
        
        setZoom(newZoom);
        setPan(newPan);
    }, []);

    // Zoom control functions
    const handleZoomIn = () => {
        if (!containerRef.current) return;
        
        const currentZoom = zoomRef.current;
        const currentPan = panRef.current;
        // Multiplicative zoom: always 50% increase
        const newZoom = Math.min(currentZoom * 1.5, BLUEPRINT_MAX_ZOOM);
        
        // Zoom relative to center of container
        const rect = containerRef.current.getBoundingClientRect();
        const centerX = rect.width / 2;
        const centerY = rect.height / 2;
        
        const pointX = (centerX - currentPan.x) / currentZoom;
        const pointY = (centerY - currentPan.y) / currentZoom;
        
        const newPan = {
            x: centerX - pointX * newZoom,
            y: centerY - pointY * newZoom
        };
        
        setZoom(newZoom);
        setPan(newPan);
    };

    const handleZoomOut = () => {
        if (!containerRef.current) return;
        
        const currentZoom = zoomRef.current;
        const currentPan = panRef.current;
        // Multiplicative zoom: always 33% decrease (inverse of 1.5x)
        const newZoom = Math.max(currentZoom / 1.5, MIN_ZOOM);
        
        // Zoom relative to center of container
        const rect = containerRef.current.getBoundingClientRect();
        const centerX = rect.width / 2;
        const centerY = rect.height / 2;
        
        const pointX = (centerX - currentPan.x) / currentZoom;
        const pointY = (centerY - currentPan.y) / currentZoom;
        
        const newPan = {
            x: centerX - pointX * newZoom,
            y: centerY - pointY * newZoom
        };
        
        setZoom(newZoom);
        setPan(newPan);
    };

    const handleResetZoom = () => {
        setZoom(1);
        setPan({ x: 0, y: 0 });
    };

    // Client point → BlueprintLocation in the drawing's pixel space (plan §3.3):
    // a fraction of the drawn box (its rect already includes zoom and pan) times
    // the node's width/height. Never the raster's naturalWidth: at high zoom the
    // vector canvas is what is drawn, and the node size is the marker reference.
    const referenceWidth = blueprint?.width ?? 0;
    const referenceHeight = blueprint?.height ?? 0;
    const locationFromClientPoint = useCallback((clientX: number, clientY: number, clamp: boolean): BlueprintLocation | null => {
        const box = imageContainerRef.current;
        if (!box) return null;
        const rect = box.getBoundingClientRect();
        return locationFromClientPointMath(
            clientX,
            clientY,
            rect,
            { width: referenceWidth, height: referenceHeight },
            currentBlueprintKey,
            clamp
        );
    }, [currentBlueprintKey, referenceWidth, referenceHeight]);

    // Move mode: press on a spot marker starts a drag; document-level listeners
    // follow the pointer outside the container and end the drag on release.
    const handleSpotDragStart = useCallback((spotKey: string, e: React.MouseEvent) => {
        if (!moveModeOn || e.button !== 0) return;
        e.preventDefault();
        e.stopPropagation();
        spotDragRef.current = { key: spotKey, startX: e.clientX, startY: e.clientY, moved: false, location: null };
    }, [moveModeOn]);

    useEffect(() => {
        if (!moveModeOn) return;

        const handleMove = (e: MouseEvent) => {
            const drag = spotDragRef.current;
            if (!drag) return;
            if (!drag.moved) {
                if (Math.abs(e.clientX - drag.startX) < 4 && Math.abs(e.clientY - drag.startY) < 4) return;
                drag.moved = true;
            }
            const location = locationFromClientPoint(e.clientX, e.clientY, true);
            if (!location) return;
            drag.location = location;
            setSpotDrag({ key: drag.key, location, dropped: false });
        };

        const handleUp = () => {
            const drag = spotDragRef.current;
            if (!drag) return;
            spotDragRef.current = null;
            if (drag.moved && drag.location) {
                // The click event that follows the release must not re-select/toggle.
                suppressSpotClickRef.current = true;
                // Optimistic: keep showing the drop position; cleared once the
                // stored location matches (effect below) or move mode ends.
                setSpotDrag({ key: drag.key, location: drag.location, dropped: true });
                onSpotMoveRef.current?.(drag.key, drag.location);
            } else {
                setSpotDrag(null);
            }
        };

        document.addEventListener('mousemove', handleMove);
        document.addEventListener('mouseup', handleUp);
        return () => {
            document.removeEventListener('mousemove', handleMove);
            document.removeEventListener('mouseup', handleUp);
            spotDragRef.current = null;
            setSpotDrag(null);
        };
    }, [moveModeOn, locationFromClientPoint]);

    // Clear the optimistic drop override once the spots prop reflects it.
    useEffect(() => {
        if (!spotDrag?.dropped) return;
        const stored = spots.find(s => s.key === spotDrag.key)?.blueprintLocations?.[currentBlueprintKey];
        if (stored && stored.x === spotDrag.location.x && stored.y === spotDrag.location.y) {
            setSpotDrag(null);
        }
    }, [spots, currentBlueprintKey, spotDrag]);

    // Placement click: convert the click to the image's pixel space using the
    // rendered <img> rect (which already includes zoom and pan).
    const handlePlacementClick = useCallback((e: React.MouseEvent) => {
        if (!placementMode || !onBlueprintClick) return;
        if (dragMovedRef.current) return;
        // Markers and the zoom controls are inside the container; neither is a placement.
        if ((e.target as HTMLElement).closest('.marker, .blueprint-zoom-controls, button')) return;
        const location = locationFromClientPoint(e.clientX, e.clientY, false);
        if (location) onBlueprintClick(location);
    }, [placementMode, onBlueprintClick, locationFromClientPoint]);

    const getScaledXY = useCallback((blueprintLocation: BlueprintLocation) => {
        if (!blueprintLocation || !blueprint || !containerDimensions.width || !containerDimensions.height) return null;

        const { x = 0, y = 0, width: locationWidth, height: locationHeight } = blueprintLocation;
        const { width: containerWidth, height: containerHeight } = containerDimensions;

        let scaledX: number;
        let scaledY: number;

        if (singleRoom && (!locationWidth || !locationHeight)) {
            const { width: blueprintWidth, height: blueprintHeight } = blueprint;
            if (!blueprintWidth || !blueprintHeight) return null;
            const scaleX = containerWidth / blueprintWidth;
            const scaleY = containerHeight / blueprintHeight;
            scaledX = x * scaleX;
            scaledY = y * scaleY;
        } else {
            if (!locationWidth || !locationHeight) return null;
            const scale = containerWidth / locationWidth;
            scaledX = x * scale;
            scaledY = y * scale;
        }

        return { x: scaledX, y: scaledY };
    }, [containerDimensions, blueprint, singleRoom]);

    const calculateMarkerPosition = useCallback((blueprintLocation: BlueprintLocation) => {
        const coords = getScaledXY(blueprintLocation);
        if (!coords) return null;
        return {
            left: `${coords.x}px`,
            top: `${coords.y}px`,
        };
    }, [getScaledXY]);


    // Helper to get icon paths with fallback for components (uses shared utility)
    const getIconPaths = useCallback((specificTypeValue: string | undefined, componentBaseType: any) => {
        return getComponentIconPaths(specificTypeValue, componentBaseType, getBaseType);
    }, [getBaseType]);

    // Generate marker data for all markers
    const generateMarkerData = React.useCallback((): MarkerData[] => {
        const markers: MarkerData[] = [];

        // Marker size constants — rooms and components share a single size,
        // matching the component marker size used on the LevelPage.
        // compactMarkers shrinks the pin about 20% (47px → 37px including the border).
        const MARKER_SIZE = compactMarkers ? 28 : 35;
        const ICON_SIZE = compactMarkers ? 19 : 25;
        const MARKER_PADDING = compactMarkers ? 6 : 8;

        // Highlight: the singled-out marker is enlarged like a selection, all others fade.
        const isHighlighted = (kind: BlueprintHighlight['kind'], key: string) =>
            !!highlight && highlight.kind === kind && highlight.key === key;
        const isDimmed = (kind: BlueprintHighlight['kind'], key: string) =>
            !!highlight && !isHighlighted(kind, key);

        // Single room marker - just a position dot
        if (singleRoom) {
            markers.push({
                key: `room-${singleRoom.room.key}`,
                blueprintLocation: singleRoom.blueprintLocation,
                type: 'position',
                displayName: singleRoom.room.displayName(),
                isSelected: false,
                iconPath: '',
                fallbackIconPaths: [],
                baseZIndex: 5,  // Below component markers
                markerSize: 16,
                iconSize: 0,
                padding: 0
            });
        } else {
            // Multiple room markers
            const roomsWithCurrentBlueprintLocation = rooms.filter(room =>
                room.blueprintLocations && room.blueprintLocations[currentBlueprintKey]
            );

            roomsWithCurrentBlueprintLocation.forEach(room => {
                const roomType = room.type ?? RoomType.RoomGeneral;
                markers.push({
                    key: room.key,
                    blueprintLocation: room.blueprintLocations![currentBlueprintKey],
                    type: 'room',
                    displayName: room.displayName(),
                    isSelected: selectedRoomKey === room.key || isHighlighted('room', room.key),
                    dimmed: isDimmed('room', room.key),
                    iconPath: `/Images/rooms/${roomType}.png`,
                    fallbackIconPaths: [`/Images/rooms/${RoomType.RoomGeneral}.png`, '/Images/placeholder.png'],
                    onClick: (e) => onRoomClick?.(room.key, e),
                    onHoverChange: onRoomHover ? (hovered) => onRoomHover(hovered ? room.key : null) : undefined,
                    baseZIndex: 20,
                    markerSize: MARKER_SIZE,
                    iconSize: ICON_SIZE,
                    padding: MARKER_PADDING
                });
            });
        }

        // Point pin (building-check picker, a component's own position): the point, nothing else
        if (pointMarker) {
            markers.push({
                key: 'point-marker',
                blueprintLocation: pointMarker,
                type: 'position',
                displayName: pointMarkerLabel,
                isSelected: false,
                iconPath: '',
                fallbackIconPaths: [],
                baseZIndex: 30,
                markerSize: 16,
                iconSize: 0,
                padding: 0
            });
        }

        // Utility/component markers - use type guard for proper typing
        const utilitiesWithCurrentBlueprintLocation = utilities
            .filter(hasBlueprintLocations)
            .filter(utility => utility.blueprintLocations[currentBlueprintKey]);

        utilitiesWithCurrentBlueprintLocation.forEach(utility => {
            const specificTypeValue = (utility as any).specificType?.value as string | undefined;
            const baseType = utility.type;
            const utilityName = utility.displayName(building);
            const iconPaths = getIconPaths(specificTypeValue, baseType);
            const muted = !!mutedUtilityKeys?.has(utility.key);
            markers.push({
                key: `utility-${utility.key}`,
                blueprintLocation: utility.blueprintLocations[currentBlueprintKey],
                type: 'utility',
                baseType: baseType,
                displayName: utilityName,
                isSelected: selectedUtilityKey === utility.key || !!selectedUtilityKeys?.has(utility.key) || isHighlighted('utility', utility.key),
                dimmed: muted || isDimmed('utility', utility.key),
                iconPath: iconPaths.primary,
                fallbackIconPaths: iconPaths.fallbacks,
                onClick: muted ? undefined : (e) => onUtilityClick?.(utility.key, e),
                onHoverChange: onUtilityHover && !muted ? (hovered) => onUtilityHover(hovered ? utility.key : null) : undefined,
                baseZIndex: 10,
                markerSize: MARKER_SIZE,
                iconSize: ICON_SIZE,
                padding: MARKER_PADDING
            });
        });

        // Numbered markers (parking spots, storage units, units), keyed by the drawing key
        spots
            .filter(spot => spot.blueprintLocations && spot.blueprintLocations[currentBlueprintKey])
            .forEach(spot => {
                const isDragging = spotDrag?.key === spot.key;
                markers.push({
                    key: `spot-${spot.key}`,
                    blueprintLocation: isDragging && spotDrag ? spotDrag.location : spot.blueprintLocations![currentBlueprintKey],
                    type: 'numbered',
                    spot,
                    displayName: spot.displayName ?? (spot.number || '?'),
                    isSelected: selectedSpotKey === spot.key || (isDragging && !spotDrag?.dropped) || isHighlighted('spot', spot.key),
                    dimmed: isDimmed('spot', spot.key),
                    iconPath: '',
                    fallbackIconPaths: [],
                    onClick: (e) => {
                        if (suppressSpotClickRef.current) {
                            suppressSpotClickRef.current = false;
                            return;
                        }
                        onSpotClick?.(spot.key, e);
                    },
                    onDragStart: moveModeOn && spot.movable !== false ? (e) => handleSpotDragStart(spot.key, e) : undefined,
                    onHoverChange: onSpotHover ? (hovered) => onSpotHover(hovered ? spot.key : null) : undefined,
                    baseZIndex: 15,
                    markerSize: 34,
                    iconSize: 0,
                    padding: 0
                });
            });

        return markers;
    }, [
        compactMarkers,
        singleRoom,
        building,
        rooms,
        utilities,
        spots,
        currentBlueprintKey,
        selectedRoomKey,
        selectedUtilityKey,
        selectedUtilityKeys,
        mutedUtilityKeys,
        selectedSpotKey,
        spotDrag,
        pointMarker,
        pointMarkerLabel,
        highlight,
        onRoomClick,
        onUtilityClick,
        onRoomHover,
        onUtilityHover,
        onSpotClick,
        onSpotHover,
        moveModeOn,
        handleSpotDragStart,
        getIconPaths
    ]);

    const markerData = generateMarkerData();

    const sortedMarkerData = React.useMemo(() => {
        const withCoords = markerData
            .map(m => {
                const c = getScaledXY(m.blueprintLocation);
                return c ? { ...m, _x: c.x, _y: c.y } : null;
            })
            .filter((m): m is MarkerData & { _x: number; _y: number } => !!m);

        // Sort by z-index first (higher values on top), then by position.
        // Numbered markers stack from upper-left to lower-right (x + y), as on iOS.
        withCoords.sort((a, b) => {
            if (a.baseZIndex !== b.baseZIndex) {
                return a.baseZIndex - b.baseZIndex; // Lower z-index first (rendered first)
            }
            if (a.type === 'numbered' && b.type === 'numbered') {
                return (a._x + a._y) - (b._x + b._y);
            }
            return (a._y - b._y) || (a._x - b._x);
        });

        return withCoords;
    }, [markerData, getScaledXY]);

    // Update container dimensions.
    // Re-run when the container actually exists: while the image is loading the
    // component renders a skeleton without the ref, so a mount-only effect never
    // attached the observer. Without it, anything that changes the layout width
    // without a window resize (a page scrollbar appearing, a side panel opening)
    // shrinks the drawing while markers keep their stale pixel positions.
    useEffect(() => {
        const updateDimensions = () => {
            if (containerRef.current) {
                setContainerDimensions({
                    width: containerRef.current.offsetWidth,
                    height: containerRef.current.offsetHeight
                });
            }
        };

        const resizeObserver = new ResizeObserver(updateDimensions);

        if (containerRef.current) {
            resizeObserver.observe(containerRef.current);
        }

        window.addEventListener('resize', updateDimensions);
        updateDimensions();

        return () => {
            resizeObserver.disconnect();
            window.removeEventListener('resize', updateDimensions);
        };
    }, [isLoading, hasError, src]);

    useEffect(() => {
        const element = containerRef.current;
        if (!element || typeof IntersectionObserver === 'undefined') {
            setNearViewport(true);
            return;
        }
        const observer = new IntersectionObserver(
            entries => {
                const entry = entries[entries.length - 1];
                if (entry) setNearViewport(entry.isIntersecting);
            },
            { rootMargin: '200px' }
        );
        observer.observe(element);
        return () => observer.disconnect();
    }, [isLoading, hasError, src]);


    if (isLoading) {
        const aspectRatio = (blueprint?.width && blueprint?.height)
            ? blueprint.height / blueprint.width
            : 0.7;
        return (
            <div>
                {!hideTitle && <h4 style={{marginBottom: '20px'}}>{title}</h4>}
                <div
                    style={{
                        width: '100%',
                        paddingBottom: `${aspectRatio * 100}%`,
                        borderRadius: '10px',
                        background: 'linear-gradient(110deg, #e8e8e8 30%, #f5f5f5 50%, #e8e8e8 70%)',
                        backgroundSize: '200% 100%',
                        animation: 'blueprintSkeletonShimmer 1.5s ease-in-out infinite',
                    }}
                />
                <style>{`
                    @keyframes blueprintSkeletonShimmer {
                        0% { background-position: 200% 0; }
                        100% { background-position: -200% 0; }
                    }
                `}</style>
            </div>
        );
    }

    if (hasError || !src) {
        return <div>Ritning saknas</div>;
    }


    return (
        <div>
            {!hideTitle && <h4 style={{marginBottom: '20px'}}>{title}</h4>}

            <div
                ref={containerRef}
                className="blueprint-container"
                style={{
                    position: 'relative',
                    width: '100%',
                    margin: 0,
                    overflow: 'hidden',
                    borderRadius: '10px',
                    cursor: disableZoom ? 'default' : (isDragging ? 'grabbing' : (placementMode ? 'crosshair' : 'grab')),
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                    userSelect: 'none',
                    WebkitUserSelect: 'none',
                    MozUserSelect: 'none',
                    msUserSelect: 'none'
                }}
                onMouseDown={disableZoom ? undefined : handleMouseDown}
                onMouseMove={disableZoom ? undefined : handleMouseMove}
                onMouseUp={disableZoom ? undefined : handleMouseUp}
                onMouseLeave={disableZoom ? undefined : handleMouseUp}
                onDoubleClick={disableZoom ? undefined : handleDoubleClick}
                onClick={placementMode ? handlePlacementClick : undefined}
            >
                {/* Zoom Controls — swallow clicks so placement mode never sees them */}
                {!disableZoom && <div className="blueprint-zoom-controls" onClick={(e) => e.stopPropagation()} onDoubleClick={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()} style={{
                    position: 'absolute',
                    top: '10px',
                    right: '10px',
                    zIndex: 1000,
                    display: 'flex',
                    gap: '5px',
                    backgroundColor: 'white',
                    borderRadius: '6px',
                    padding: '5px',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)'
                }}>
                    <button
                        onClick={handleZoomIn}
                        disabled={zoom >= maxZoom}
                        style={{
                            width: '28px',
                            height: '28px',
                            border: '1px solid #ddd',
                            borderRadius: '4px',
                            backgroundColor: zoom >= maxZoom ? '#f5f5f5' : 'white',
                            cursor: zoom >= maxZoom ? 'not-allowed' : 'pointer',
                            fontSize: '16px',
                            fontWeight: 'bold',
                            color: zoom >= maxZoom ? '#ccc' : '#333',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            transition: 'all 0.2s'
                        }}
                        title="Zooma in"
                    >
                        +
                    </button>
                    <button
                        onClick={handleZoomOut}
                        disabled={zoom <= 0.5}
                        style={{
                            width: '28px',
                            height: '28px',
                            border: '1px solid #ddd',
                            borderRadius: '4px',
                            backgroundColor: zoom <= 0.5 ? '#f5f5f5' : 'white',
                            cursor: zoom <= 0.5 ? 'not-allowed' : 'pointer',
                            fontSize: '16px',
                            fontWeight: 'bold',
                            color: zoom <= 0.5 ? '#ccc' : '#333',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            transition: 'all 0.2s'
                        }}
                        title="Zooma ut"
                    >
                        −
                    </button>
                    <button
                        onClick={handleResetZoom}
                        disabled={zoom === 1 && pan.x === 0 && pan.y === 0}
                        style={{
                            width: '28px',
                            height: '28px',
                            border: '1px solid #ddd',
                            borderRadius: '4px',
                            backgroundColor: (zoom === 1 && pan.x === 0 && pan.y === 0) ? '#f5f5f5' : 'white',
                            cursor: (zoom === 1 && pan.x === 0 && pan.y === 0) ? 'not-allowed' : 'pointer',
                            fontSize: '16px',
                            fontWeight: 'bold',
                            color: (zoom === 1 && pan.x === 0 && pan.y === 0) ? '#ccc' : '#333',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            transition: 'all 0.2s'
                        }}
                        title="Återställ"
                    >
                        ⟲
                    </button>
                </div>}

                <div
                    ref={imageContainerRef}
                    style={{
                        transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
                        transformOrigin: '0 0',
                        transition: isDragging ? 'none' : 'transform 0.1s ease-out',
                        position: 'relative',
                        width: '100%',
                        height: 'auto'
                    }}
                >
                    <img
                        ref={imageRef}
                        src={src}
                        alt="Blueprint"
                        className="blueprint-image"
                        onLoad={() => {
                            if (containerRef.current) {
                                setContainerDimensions({
                                    width: containerRef.current.offsetWidth,
                                    height: containerRef.current.offsetHeight
                                });
                            }
                        }}
                        style={{
                            width: '100%',
                            height: 'auto',
                            display: 'block',
                            pointerEvents: 'none'
                        }}
                    />

                    {/* Vector layer over the raster placeholder; the raster stays visible until the first render and when the PDF fails */}
                    {pdfPage && nearViewport && referenceWidth > 0 && referenceHeight > 0 && containerDimensions.width > 0 && (
                        <PdfPageCanvas
                            page={pdfPage}
                            boxWidth={containerDimensions.width}
                            boxHeight={containerDimensions.width * referenceHeight / referenceWidth}
                            zoom={zoom}
                            pan={pan}
                            containerWidth={containerDimensions.width}
                            containerHeight={containerDimensions.height}
                        />
                    )}

                    {/* Render all markers */}
                    {sortedMarkerData.map(marker => (
                        <Marker
                            key={marker.key}
                            data={marker}
                            position={calculateMarkerPosition(marker.blueprintLocation)}
                            zoom={zoom}
                            containerRef={containerRef}
                        />
                    ))}
                </div>
            </div>
        </div>
    );
};

export default BlueprintWithMarkers;